import React, { useMemo, useState } from 'react';
import { GAME_MODES } from '../data/siteContent';
import { useSiteContext } from '../context/SiteContext';

type ModeFilter = 'all' | string;

const LeaderboardPage: React.FC = () => {
  const { profile, scores } = useSiteContext();
  const [modeFilter, setModeFilter] = useState<ModeFilter>('all');

  const filteredScores = useMemo(() => {
    return scores.filter((entry) => modeFilter === 'all' || entry.modeId === modeFilter);
  }, [modeFilter, scores]);

  const playerBest = useMemo(() => {
    if (!profile) {
      return 0;
    }

    return filteredScores
      .filter((entry) => entry.nickname === profile.nickname)
      .reduce((bestScore, entry) => Math.max(bestScore, entry.score), 0);
  }, [filteredScores, profile]);

  return (
    <div className="page">
      <section className="page-heading">
        <div>
          <p className="eyebrow">Leaderboard</p>
          <h1>Rank every run on the board.</h1>
          <p>
            Track the strongest performances, filter by mode, and keep the competition visible
            across the site.
          </p>
        </div>
        <div className="page-heading__meta">
          <span className="site-chip">Visible scores: {filteredScores.length}</span>
          <span className="site-chip">Your filtered best: {playerBest}</span>
        </div>
      </section>

      <section className="leaderboard-toolbar">
        <button
          type="button"
          className={`filter-chip ${modeFilter === 'all' ? 'filter-chip--active' : ''}`}
          onClick={() => setModeFilter('all')}
        >
          All modes
        </button>
        {GAME_MODES.map((mode) => (
          <button
            key={mode.id}
            type="button"
            className={`filter-chip ${modeFilter === mode.id ? 'filter-chip--active' : ''}`}
            onClick={() => setModeFilter(mode.id)}
          >
            {mode.name}
          </button>
        ))}
      </section>

      <section className="leaderboard-table">
        <div className="leaderboard-table__head">
          <span>Rank</span>
          <span>Player</span>
          <span>Mode</span>
          <span>Score</span>
          <span>When</span>
        </div>

        {filteredScores.length > 0 ? (
          filteredScores.map((entry, index) => (
            <article key={entry.id} className="leaderboard-row">
              <span className="leaderboard-row__rank">#{index + 1}</span>
              <span>{entry.nickname}</span>
              <span>{GAME_MODES.find((mode) => mode.id === entry.modeId)?.name || entry.modeId}</span>
              <strong>{entry.score}</strong>
              <span>{new Date(entry.createdAt).toLocaleDateString()}</span>
            </article>
          ))
        ) : (
          <div className="empty-state empty-state--panel">
            No saved runs match this filter yet. Play a round to populate the leaderboard.
          </div>
        )}
      </section>
    </div>
  );
};

export default LeaderboardPage;
